import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollText, Trash2, ExternalLink, MapPin, Store, CalendarDays, Search, FileDown, FileSpreadsheet, CheckSquare, CheckCircle2, Circle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, useCabang } from "@/context/AuthContext";
import { formatIDR, todayISO } from "@/lib/format";
import { exportDetail } from "@/lib/exportDetail";

const TABS = [
  { key: "semua", label: "Semua" },
  { key: "bahan_baku", label: "Bahan Baku", path: "bahan-baku" },
  { key: "ops", label: "Operasional", path: "ops" },
  { key: "insentif", label: "Insentif Usaha", path: "insentif" },
  { key: "lainnya", label: "Lainnya", path: "lainnya" },
];

const CAT_LABEL = { bahan_baku: "Bahan Baku", ops: "Operasional", insentif: "Insentif", lainnya: "Lainnya" };
const CAT_TONE = {
  bahan_baku: "bg-blue-100 text-blue-700 border-blue-200",
  ops: "bg-amber-100 text-amber-700 border-amber-200",
  insentif: "bg-emerald-100 text-emerald-700 border-emerald-200",
  lainnya: "bg-slate-100 text-slate-700 border-slate-200",
};

function firstOfMonth() {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

export default function Riwayat() {
  const { user } = useAuth();
  const { cabangList } = useCabang();
  const isSuper = user?.role === "super_admin";
  const isCabangUser = user?.role === "cabang";
  const isViewer = user?.role === "viewer";
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialTab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "semua";
  const [tab, setTab] = useState(initialTab);

  const [admins, setAdmins] = useState([]);
  const [adminId, setAdminId] = useState("");
  const [cabangId, setCabangId] = useState(isCabangUser ? user?.cabang_id : "all");
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayISO());
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState({ bahan_baku: [], ops: [], insentif: [], lainnya: [] });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set()); // set of "${cat}-${id}"

  const toggleSelect = (cat, id) => {
    const key = `${cat}-${id}`;
    setSelected((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };
  const clearSelection = () => setSelected(new Set());

  useEffect(() => {
    if (!isSuper) return;
    api.get("/users")
      .then(({ data }) => setAdmins(data.filter((u) => u.role === "admin" && (u.workspace_id === user?.active_workspace_id || u.active_workspace_id === user?.active_workspace_id))))
      .catch(() => setAdmins([]));
  }, [isSuper, user?.active_workspace_id]);

  const visibleCabang = useMemo(() => {
    if (!isSuper) return cabangList;
    if (!adminId) return cabangList;
    return cabangList.filter((c) => c.admin_id === adminId);
  }, [cabangList, isSuper, adminId]);

  const load = async () => {
    setLoading(true);
    try {
      const p = { tanggal_from: from, tanggal_to: to };
      if (cabangId && cabangId !== "all") p.cabang_id = cabangId;
      if (isSuper && adminId) p.admin_id = adminId;
      const { data } = await api.get("/dashboard/detail", { params: p });
      setDetail(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memuat");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cabangId, adminId, from, to]);

  const handleTabChange = (v) => {
    setTab(v);
    const p = new URLSearchParams(params); p.set("tab", v); setParams(p, { replace: true });
  };

  const nameCabang = (id) => cabangList.find((c) => c.id === id)?.name || "—";
  const nameByCat = (r, cat) => cat === "insentif" ? (r.nama || "—") : (r.item || "—");
  const realOf = (r, cat) => cat === "insentif" ? Number(r.realisasi || 0) : Number(r.pembelian || 0);
  const angOf = (r, cat) => cat === "lainnya" ? 0 : Number(r.anggaran || 0);

  const deleteRow = async (cat, row) => {
    const path = TABS.find((t) => t.key === cat)?.path;
    if (!path) return;
    // Optimistic delete + Undo toast (5 detik)
    setDetail((d) => ({ ...d, [cat]: d[cat].filter((r) => r.id !== row.id) }));
    let undone = false;
    toast("Baris dihapus", {
      action: { label: "Undo", onClick: () => { undone = true; } },
      duration: 5000,
    });
    await new Promise((r) => setTimeout(r, 5000));
    if (undone) { toast.success("Dibatalkan"); load(); return; }
    try { await api.delete(`/${path}/${row.id}`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Gagal menghapus"); load(); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Hapus ${selected.size} baris terpilih? Tindakan ini tidak bisa di-undo untuk bulk.`)) return;
    const items = Array.from(selected).map((k) => { const [c, ...rest] = k.split("-"); return { cat: c, id: rest.join("-") }; });
    // Coba fallback: format "cat-id" tapi cat bisa punya underscore ("bahan_baku")
    const parsed = items.map((it) => {
      const catKey = ["bahan_baku", "ops", "insentif", "lainnya"].find((c) => `${c}-`.length && `${c}-` === `${it.cat}-` || `${c}_${it.id.split("-")[0]}`);
      return { cat: catKey || it.cat, id: it.id };
    });
    let done = 0, fail = 0;
    for (const k of selected) {
      // Parse: format catKey-rowId. catKey mengandung underscore untuk bahan_baku.
      let cat, id;
      if (k.startsWith("bahan_baku-")) { cat = "bahan_baku"; id = k.slice("bahan_baku-".length); }
      else if (k.startsWith("ops-")) { cat = "ops"; id = k.slice(4); }
      else if (k.startsWith("insentif-")) { cat = "insentif"; id = k.slice(9); }
      else if (k.startsWith("lainnya-")) { cat = "lainnya"; id = k.slice(8); }
      else continue;
      const path = TABS.find((t) => t.key === cat)?.path;
      try { await api.delete(`/${path}/${id}`); done++; }
      catch { fail++; }
    }
    toast.success(`${done} baris dihapus${fail ? ` (${fail} gagal)` : ""}`);
    clearSelection();
    load();
  };

  const gotoInputHarian = (cbg, tgl) => {
    navigate(`/input-harian?cabang=${cbg}&tanggal=${tgl}`);
  };

  const togglePaid = async (cat, row) => {
    const willPay = !row.is_paid;
    const url = `/tagihan/${cat}/${row.id}/${willPay ? "pay" : "unpay"}`;
    // Optimistic update
    setDetail((d) => ({
      ...d,
      [cat]: (d[cat] || []).map((r) => r.id === row.id ? { ...r, is_paid: willPay } : r),
    }));
    try {
      await api.post(url);
      toast.success(willPay ? "Ditandai lunas" : "Ditandai belum bayar");
    } catch (err) {
      // rollback
      setDetail((d) => ({
        ...d,
        [cat]: (d[cat] || []).map((r) => r.id === row.id ? { ...r, is_paid: !willPay } : r),
      }));
      toast.error(err?.response?.data?.detail || "Gagal update status");
    }
  };

  const q = search.trim().toLowerCase();
  const filterFn = (r, cat) => {
    if (!q) return true;
    const name = nameByCat(r, cat);
    return String(name).toLowerCase().includes(q) || String(r.catatan || "").toLowerCase().includes(q);
  };

  // Kombinasi untuk tab "semua"
  const allRows = useMemo(() => {
    const rows = [];
    ["bahan_baku", "ops", "insentif", "lainnya"].forEach((cat) => {
      (detail[cat] || []).forEach((r) => rows.push({ ...r, _cat: cat }));
    });
    return rows.sort((a, b) => String(b.tanggal || "").localeCompare(String(a.tanggal || "")));
  }, [detail]);

  const doExport = (format) => {
    const activeCat = tab === "semua" ? null : tab;
    if (activeCat) {
      const rows = (detail[activeCat] || []).filter((r) => filterFn(r, activeCat)).map((r) => ({
        tanggal: r.tanggal,
        nama_item: nameByCat(r, activeCat),
        jumlah: r.jumlah ?? "",
        anggaran: angOf(r, activeCat),
        realisasi: realOf(r, activeCat),
        selisih: angOf(r, activeCat) - realOf(r, activeCat),
        cabang: nameCabang(r.cabang_id),
        catatan: r.catatan || "",
      }));
      const catLabel = CAT_LABEL[activeCat];
      exportDetail({
        format, title: `Riwayat ${catLabel} (${from} s/d ${to})`,
        filename: `Riwayat_${catLabel}_${from}_${to}`,
        columns: [
          { key: "tanggal", label: "Tanggal" },
          { key: "nama_item", label: activeCat === "insentif" ? "Nama" : "Item" },
          ...(activeCat === "insentif" || activeCat === "lainnya" ? [] : [{ key: "jumlah", label: "Jumlah", type: "number" }]),
          { key: "anggaran", label: "Penjualan", type: "money" },
          { key: "realisasi", label: activeCat === "insentif" ? "Realisasi" : "Pembelian", type: "money" },
          { key: "selisih", label: "Selisih", type: "money" },
          { key: "cabang", label: "Cabang" },
          { key: "catatan", label: "Catatan" },
        ],
        rows,
      });
    } else {
      const rows = allRows.filter((r) => filterFn(r, r._cat)).map((r) => ({
        kategori: CAT_LABEL[r._cat], tanggal: r.tanggal,
        nama_item: nameByCat(r, r._cat),
        anggaran: angOf(r, r._cat), realisasi: realOf(r, r._cat),
        selisih: angOf(r, r._cat) - realOf(r, r._cat),
        cabang: nameCabang(r.cabang_id), catatan: r.catatan || "",
      }));
      exportDetail({
        format, title: `Riwayat Semua Kategori (${from} s/d ${to})`,
        filename: `Riwayat_Semua_${from}_${to}`,
        columns: [
          { key: "kategori", label: "Kategori" }, { key: "tanggal", label: "Tanggal" },
          { key: "nama_item", label: "Item / Nama" },
          { key: "anggaran", label: "Penjualan", type: "money" },
          { key: "realisasi", label: "Realisasi", type: "money" },
          { key: "selisih", label: "Selisih", type: "money" },
          { key: "cabang", label: "Cabang" }, { key: "catatan", label: "Catatan" },
        ],
        rows,
      });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={<span className="inline-flex items-center gap-2"><ScrollText className="h-6 w-6 text-emerald-600" /> Riwayat Transaksi</span>}
        subtitle="Lihat, filter, dan kelola data lintas kategori. Untuk menambah/mengedit isi baris, gunakan Input Data Harian."
        actions={
          <div className="flex items-center gap-2">
            {!isViewer && (
              <>
                <Button variant="outline" onClick={() => doExport("excel")} data-testid="export-excel-btn" className="border-slate-300">
                  <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
                </Button>
                <Button variant="outline" onClick={() => doExport("pdf")} data-testid="export-pdf-btn" className="border-slate-300">
                  <FileDown className="h-4 w-4 mr-2" /> PDF
                </Button>
              </>
            )}
            {!isViewer && selected.size > 0 && (
              <Button onClick={bulkDelete} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="bulk-delete-btn">
                <Trash2 className="h-4 w-4 mr-2" /> Hapus Terpilih ({selected.size})
              </Button>
            )}
          </div>
        }
      />

      {/* Filter bar */}
      <Card className="p-4 border-slate-200">
        <div className={`grid grid-cols-1 md:grid-cols-2 ${isSuper ? "xl:grid-cols-5" : "xl:grid-cols-4"} gap-3`}>
          {isSuper && (
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1"><Store className="h-3 w-3" /> Usaha</Label>
              <Select value={adminId || "all"} onValueChange={(v) => setAdminId(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-usaha" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">Semua Usaha</SelectItem>
                  {admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> Cabang</Label>
            <Select value={cabangId} onValueChange={setCabangId} disabled={isCabangUser}>
              <SelectTrigger data-testid="filter-cabang" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Semua Cabang</SelectItem>
                {visibleCabang.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> Dari</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="filter-from" className="h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> Sampai</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="filter-to" className="h-9" />
          </div>
          <div className="space-y-1 md:col-span-2 xl:col-span-1">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1"><Search className="h-3 w-3" /> Cari</Label>
            <Input placeholder="Nama item / catatan..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="filter-search" className="h-9" />
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList className="bg-slate-100 p-1 h-auto flex-wrap">
          {TABS.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}>{t.label}</TabsTrigger>)}
        </TabsList>

        {/* Semua */}
        <TabsContent value="semua">
          <Card className="p-0 overflow-hidden border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Kategori</TableHead>
                  <TableHead>Tanggal</TableHead>
                  <TableHead>Item / Nama</TableHead>
                  <TableHead className="text-right">Penjualan</TableHead>
                  <TableHead className="text-right">Realisasi</TableHead>
                  <TableHead className="text-right">Selisih</TableHead>
                  <TableHead>Bayar</TableHead>
                  <TableHead>Cabang</TableHead>
                  <TableHead className="w-24 text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="riwayat-semua-tbody">
                {loading ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-500">Memuat...</TableCell></TableRow>
                ) : allRows.filter((r) => filterFn(r, r._cat)).length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-10 text-slate-400">Tidak ada data untuk filter yang dipilih</TableCell></TableRow>
                ) : allRows.filter((r) => filterFn(r, r._cat)).map((r) => {
                  const sel = angOf(r, r._cat) - realOf(r, r._cat);
                  const key = `${r._cat}-${r.id}`;
                  return (
                    <TableRow key={key} data-testid={`row-${r._cat}-${r.id}`}>
                      <TableCell>
                        <Checkbox checked={selected.has(key)} onCheckedChange={() => toggleSelect(r._cat, r.id)}
                                  data-testid={`select-${r._cat}-${r.id}`} />
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 border ${CAT_TONE[r._cat]}`}>{CAT_LABEL[r._cat]}</span>
                      </TableCell>
                      <TableCell className="text-sm">{r.tanggal}</TableCell>
                      <TableCell className="text-sm font-medium">{nameByCat(r, r._cat)}</TableCell>
                      <TableCell className="text-sm text-right">{formatIDR(angOf(r, r._cat))}</TableCell>
                      <TableCell className="text-sm text-right">{formatIDR(realOf(r, r._cat))}</TableCell>
                      <TableCell className={`text-sm text-right font-semibold ${sel >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{formatIDR(sel)}</TableCell>
                      <TableCell>
                        <PaidToggle row={r} cat={r._cat} isViewer={isViewer} onToggle={togglePaid} />
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{nameCabang(r.cabang_id)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => gotoInputHarian(r.cabang_id, r.tanggal)} title="Buka di Input Harian" data-testid={`goto-${r._cat}-${r.id}`}>
                            <ExternalLink className="h-4 w-4 text-slate-600" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteRow(r._cat, r)} title="Hapus" data-testid={`delete-${r._cat}-${r.id}`}>
                            <Trash2 className="h-4 w-4 text-rose-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Per kategori */}
        {["bahan_baku", "ops", "insentif", "lainnya"].map((cat) => (
          <TabsContent key={cat} value={cat}>
            <Card className="p-0 overflow-hidden border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Tanggal</TableHead>
                    <TableHead>{cat === "insentif" ? "Nama" : "Item"}</TableHead>
                    {cat !== "insentif" && cat !== "lainnya" && <TableHead className="text-right">Jumlah</TableHead>}
                    {cat !== "lainnya" && <TableHead className="text-right">Penjualan</TableHead>}
                    <TableHead className="text-right">{cat === "insentif" ? "Realisasi" : "Pembelian"}</TableHead>
                    {cat !== "lainnya" && <TableHead className="text-right">Selisih</TableHead>}
                    <TableHead>Bayar</TableHead>
                    <TableHead>Cabang</TableHead>
                    <TableHead>Catatan</TableHead>
                    <TableHead className="w-24 text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid={`riwayat-${cat}-tbody`}>
                  {loading ? (
                    <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-500">Memuat...</TableCell></TableRow>
                  ) : (detail[cat] || []).filter((r) => filterFn(r, cat)).length === 0 ? (
                    <TableRow><TableCell colSpan={10} className="text-center py-10 text-slate-400">Belum ada data</TableCell></TableRow>
                  ) : (detail[cat] || []).filter((r) => filterFn(r, cat))
                      .sort((a, b) => String(b.tanggal || "").localeCompare(String(a.tanggal || "")))
                      .map((r) => {
                    const ang = angOf(r, cat), real = realOf(r, cat), sel = ang - real;
                    return (
                      <TableRow key={r.id} data-testid={`row-${cat}-${r.id}`}>
                        <TableCell className="text-sm">{r.tanggal}</TableCell>
                        <TableCell className="text-sm font-medium">{nameByCat(r, cat)}</TableCell>
                        {cat !== "insentif" && cat !== "lainnya" && <TableCell className="text-sm text-right">{r.jumlah ?? "—"}</TableCell>}
                        {cat !== "lainnya" && <TableCell className="text-sm text-right">{formatIDR(ang)}</TableCell>}
                        <TableCell className="text-sm text-right">{formatIDR(real)}</TableCell>
                        {cat !== "lainnya" && <TableCell className={`text-sm text-right font-semibold ${sel >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{formatIDR(sel)}</TableCell>}
                        <TableCell>
                          <PaidToggle row={r} cat={cat} isViewer={isViewer} onToggle={togglePaid} />
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{nameCabang(r.cabang_id)}</TableCell>
                        <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">{r.catatan || "—"}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => gotoInputHarian(r.cabang_id, r.tanggal)} title="Buka di Input Harian" data-testid={`goto-${cat}-${r.id}`}>
                              <ExternalLink className="h-4 w-4 text-slate-600" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => deleteRow(cat, r)} title="Hapus" data-testid={`delete-${cat}-${r.id}`}>
                              <Trash2 className="h-4 w-4 text-rose-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function PaidToggle({ row, cat, isViewer, onToggle }) {
  const paid = !!row.is_paid;
  if (isViewer) {
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-1 border ${
        paid ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"
      }`} data-testid={`paid-status-${cat}-${row.id}`}>
        {paid ? <><CheckCircle2 className="h-3 w-3" /> Lunas</> : <><Circle className="h-3 w-3" /> Belum</>}
      </span>
    );
  }
  return (
    <button type="button" onClick={() => onToggle(cat, row)}
            className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-1 border transition-all hover:shadow-sm ${
              paid
                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                : "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
            }`} data-testid={`paid-toggle-${cat}-${row.id}`}
            title={paid ? "Klik untuk tandai belum bayar" : "Klik untuk tandai lunas"}>
      {paid ? <><CheckCircle2 className="h-3 w-3" /> Lunas</> : <><Circle className="h-3 w-3" /> Belum</>}
    </button>
  );
}
